/**
 * Syncs a flat filters object to/from Vue Router query params.
 *
 * Options API usage:
 *
 *   import { UrlFilters } from '@/composables/urlFilters'
 *
 *   data() {
 *     return {
 *       filters: { search: '', employee_id: '', status: '' },
 *       _urlFilters: null,
 *     }
 *   },
 *   mounted() {
 *     this._urlFilters = new UrlFilters(this.$router, this.$route, this.filters)
 *     Object.assign(this.filters, this._urlFilters.read())
 *   },
 *   methods: {
 *     applyFilters() {
 *       this._urlFilters.push(this.filters)
 *       this.loadData()
 *     },
 *     resetFilters() {
 *       Object.assign(this.filters, this._urlFilters.reset())
 *       this.loadData()
 *     }
 *   }
 */
export class UrlFilters {
  constructor(router, route, defaults) {
    this._router   = router
    this._route    = route
    this._defaults = { ...defaults }
  }

  /** Read URL query params into a filters object, falling back to defaults. */
  read() {
    const filters = { ...this._defaults }
    Object.keys(this._defaults).forEach(key => {
      if (this._route.query[key] != null && this._route.query[key] !== '') {
        filters[key] = this._route.query[key]
      }
    })
    return filters
  }

  /** Replace current URL query with active (non-empty) filter values. */
  push(filters) {
    const query = {}
    Object.keys(filters).forEach(key => {
      const v = filters[key]
      if (v !== '' && v != null) query[key] = String(v)
    })
    this._router.replace({ query }).catch(() => {})
  }

  /** Clear all filters and remove query params. Returns fresh defaults. */
  reset() {
    this._router.replace({ query: {} }).catch(() => {})
    return { ...this._defaults }
  }

  /** Number of currently active (non-empty) filters. */
  activeCount(filters) {
    return Object.values(filters).filter(v => v !== '' && v != null).length
  }
}
